import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, ArchiveRestore, RotateCcw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Progress } from "./ui/progress";
import { Skeleton } from "./ui/skeleton";
import { api, errMsg } from "../lib/api";

const METHOD_NOTE = {
  row_results: "Identified from this import's saved row results.",
  time_window: "This import predates record tagging, so its properties were identified from the import's time window "
    + "(third-party properties created by the same admin while it ran). Review the count before confirming.",
};

/**
 * Roll back a third-party bulk import: every property the job created (with its demands,
 * assignments and custom values) moves to the recycle bin, where it can be restored.
 * Properties that already received payments are never touched.
 * Shared by Bulk Entry (after stop/finish) and Upload Status (batch history).
 */
export function UndoImportDialog({ code, job, open, onOpenChange, onDone }) {
  const jobId = job?.job_id || job?.upload_id;
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [undo, setUndo] = useState(null);       // live `undo` sub-document while rolling back
  const [busy, setBusy] = useState(false);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    if (!open || !jobId) return;
    let cancelled = false;
    setPreview(null); setPreviewError(null); setUndo(null); setBusy(false);
    api.get(`/t/${code}/third-party/bulk/jobs/${jobId}/undo-preview`)
      .then((r) => {
        if (cancelled) return;
        setPreview(r.data);
        if (r.data.undo_status === "processing") setUndo({ status: "processing" });
      })
      .catch((e) => { if (!cancelled) setPreviewError(errMsg(e)); });
    return () => { cancelled = true; };
  }, [open, code, jobId]);

  // Poll the job while its undo runs on the server.
  useEffect(() => {
    if (!open || !jobId || undo?.status !== "processing") return;
    let stopped = false;
    let timer;
    const poll = async () => {
      try {
        const r = await api.get(`/t/${code}/third-party/bulk/jobs/${jobId}`, { timeout: 15000 });
        if (stopped) return;
        const u = r.data.undo || {};
        setUndo(u);
        if (u.status === "completed") {
          toast.success(`${u.moved} propert${u.moved === 1 ? "y" : "ies"} moved to the recycle bin`
            + (u.skipped_paid ? ` · ${u.skipped_paid} with payments kept` : ""));
          doneRef.current?.(u);
          return;
        }
        if (u.status === "failed") return;
      } catch { /* transient — keep polling */ }
      if (!stopped) timer = setTimeout(poll, 1000);
    };
    poll();
    return () => { stopped = true; clearTimeout(timer); };
  }, [open, code, jobId, undo?.status]);

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/third-party/bulk/jobs/${jobId}/undo`);
      setUndo(r.data.undo || { status: "processing" });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const running = undo?.status === "processing";
  const finished = undo?.status === "completed" || undo?.status === "failed";
  const pct = undo?.total ? Math.min(100, Math.round(((undo.processed || 0) / undo.total) * 100)) : 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!running) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-lg" data-testid="undo-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <RotateCcw className="h-4 w-4" /> Undo this import
          </DialogTitle>
          <DialogDescription className="break-all" data-testid="undo-import-job">
            {job?.filename || "Third-party import"} · Job ID: {jobId}
          </DialogDescription>
        </DialogHeader>

        {previewError && (
          <Alert variant="destructive" data-testid="undo-import-preview-error">
            <AlertTitle>Could not load the preview</AlertTitle>
            <AlertDescription>{previewError}</AlertDescription>
          </Alert>
        )}

        {!preview && !previewError && (
          <div className="space-y-2" data-testid="undo-import-preview-loading">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-5 w-1/2" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}

        {preview && !running && !finished && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg border p-2">
                <p className="text-lg font-semibold" data-testid="undo-import-count">{preview.will_remove}</p>
                <p className="text-[11px] text-muted-foreground">To recycle bin</p>
              </div>
              <div className="rounded-lg border p-2">
                <p className="text-lg font-semibold" data-testid="undo-import-demands">{preview.demands}</p>
                <p className="text-[11px] text-muted-foreground">Demands removed</p>
              </div>
              <div className="rounded-lg border p-2">
                <p className="text-lg font-semibold text-amber-700" data-testid="undo-import-paid">{preview.with_payments}</p>
                <p className="text-[11px] text-muted-foreground">Kept (have payments)</p>
              </div>
            </div>
            {preview.sample_pins?.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5" data-testid="undo-import-sample-pins">
                <span className="text-[11px] text-muted-foreground">e.g.</span>
                {preview.sample_pins.map((p) => (
                  <Badge key={p} variant="outline" className="font-mono text-[10px]">{p}</Badge>
                ))}
              </div>
            )}
            {preview.method === "time_window" && (
              <Alert data-testid="undo-import-method-warning">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Best-effort match</AlertTitle>
                <AlertDescription>{METHOD_NOTE.time_window}</AlertDescription>
              </Alert>
            )}
            {preview.method === "row_results" && (
              <p className="text-xs text-muted-foreground">{METHOD_NOTE.row_results}</p>
            )}
            {preview.will_remove === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="undo-import-nothing">
                Nothing left to undo — no properties from this import exist anymore
                {preview.with_payments ? ` (${preview.with_payments} kept because payments were received)` : ""}.
              </p>
            ) : (
              <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                Properties (with their demands and collector assignments) move to the recycle bin and can be
                restored anytime from Upload Status. Properties with payments are never removed.
              </p>
            )}
          </div>
        )}

        {running && (
          <div className="space-y-2" role="status" aria-live="polite" data-testid="undo-import-progress">
            <p className="text-sm font-medium">
              Rolling back… {undo.total ? `${undo.processed || 0} / ${undo.total}` : ""}
            </p>
            <Progress value={pct} className="[&>div]:transition-transform [&>div]:duration-200" />
            <p className="text-xs text-muted-foreground">Keep this window open — large imports take a few moments.</p>
          </div>
        )}

        {undo?.status === "completed" && (
          <Alert data-testid="undo-import-done">
            <ArchiveRestore className="h-4 w-4" />
            <AlertTitle>Import rolled back</AlertTitle>
            <AlertDescription>
              {undo.moved} propert{undo.moved === 1 ? "y" : "ies"} moved to the recycle bin
              {undo.skipped_paid ? `, ${undo.skipped_paid} kept because payments were received` : ""}
              {undo.missing ? `, ${undo.missing} already removed earlier` : ""}.
            </AlertDescription>
          </Alert>
        )}

        {undo?.status === "failed" && (
          <Alert variant="destructive" data-testid="undo-import-failed">
            <AlertTitle>Undo stopped with an error</AlertTitle>
            <AlertDescription>{undo.error || "Please try again."} Progress so far is kept; running Undo again continues safely.</AlertDescription>
          </Alert>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={running} data-testid="undo-import-close">
            {finished ? "Close" : "Cancel"}
          </Button>
          {!finished && (
            <Button variant="destructive" onClick={start} data-testid="undo-import-confirm"
              disabled={busy || running || !preview || preview.will_remove === 0}>
              <RotateCcw className="h-4 w-4 mr-2" />
              {running ? "Rolling back…" : preview ? `Move ${preview.will_remove} to recycle bin` : "Undo import"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default UndoImportDialog;
